import { useRouter } from 'next/router';
import { useEffect, useRef, useState, useCallback } from 'react';

import { HotjarControlState } from '@modules/hotjar/types';
import { RECORDING_TIMEOUT } from '@modules/hotjar/constants';

export const useHotjarControl = (): {
	state: HotjarControlState;
	startRecording: (reason: string) => void;
	stopRecording: (reason: string) => void;
} => {
	const router = useRouter();
	const [state, setState] = useState<HotjarControlState>({
		isRecording: true,
		sessionStartTime: Date.now(),
		currentRoute: '',
	});

	const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const initializedRef = useRef(false);

	const stopRecording = useCallback((reason: string): void => {
		setState((prev) => {
			if (typeof window !== 'undefined' && window.hj && prev.isRecording) {
				window.hj('event', 'recording-stopped', { reason });

				if (recordingTimeoutRef.current) {
					clearTimeout(recordingTimeoutRef.current);
					recordingTimeoutRef.current = null;
				}

				return {
					...prev,
					isRecording: false,
				};
			}
			return prev;
		});
	}, []);

	const startRecording = useCallback(
		(reason: string): void => {
			setState((prev) => {
				if (typeof window !== 'undefined' && window.hj && !prev.isRecording) {
					// Send virtual page view with full URL (required for Hotjar tracking)
					window.hj('stateChange', window.location.href);

					// Send event to track why recording started
					window.hj('event', 'recording-started', {
						reason,
						timestamp: Date.now(),
						route: router?.pathname || window.location.pathname,
						url: window.location.href,
					});

					recordingTimeoutRef.current = setTimeout(() => {
						stopRecording('session-timeout');
					}, RECORDING_TIMEOUT);

					return {
						...prev,
						isRecording: true,
						sessionStartTime: Date.now(),
						currentRoute: router?.pathname,
					};
				}
				return prev;
			});
		},
		[router?.pathname, stopRecording],
	);

	// Handle route changes and send stateChange to Hotjar
	useEffect(() => {
		const { pathname } = router;

		// Update current route in state
		setState((prev) => ({ ...prev, currentRoute: pathname }));

		// Send stateChange to Hotjar on every route change - Hotjar decides internally whether to record
		// We MUST always send this regardless of our internal recording state
		if (typeof window !== 'undefined' && window.hj) {
			window.hj('stateChange', window.location.href);
		}
	}, [router, state.isRecording]);

	// No automatic timeout for slots - recording continues until iframe interaction
	// The slots recording is stopped by handleIframeClick in useGameActivityDetector

	// Send initial stateChange when Hotjar becomes available
	// This effect needs to run repeatedly until window.hj is available
	useEffect(() => {
		if (!initializedRef.current) {
			if (typeof window !== 'undefined' && window.hj) {
				window.hj('stateChange', window.location.href);
				initializedRef.current = true;
			}
		}
	}, [router?.pathname, state.isRecording]); // Add state.isRecording to trigger re-check

	// Initialize recording timeout for default enabled recording
	useEffect(() => {
		if (state.isRecording && !recordingTimeoutRef.current) {
			recordingTimeoutRef.current = setTimeout(() => {
				stopRecording('session-timeout');
			}, RECORDING_TIMEOUT);
		}
	}, [state.isRecording, stopRecording]);

	useEffect((): (() => void) => {
		return (): void => {
			if (recordingTimeoutRef.current) {
				clearTimeout(recordingTimeoutRef.current);
			}
		};
	}, []);

	return {
		state,
		startRecording,
		stopRecording,
	};
};
